/**
 * @file ticketsReducer.js
 * @description Reducer global para el manejo de tickets en TicketManagerTabs.
 * Centraliza todas las mutaciones del estado relacionadas a tickets.
 */

/**
 * Estado inicial del reducer
 */
export const initialTicketsState = {
  tickets: []
};

/**
 * Reducer principal de tickets
 *
 * @param {Object} state
 * @param {Object} action
 * @returns {Object}
 */
/**
 * Reducer global de tickets
 * Migrado desde TicketListCustom
 * Versión independiente de tabs
 */

export function ticketsReducer(state, action) {
  switch (action.type) {

    /**
     * =========================
     * LOAD_TICKETS
     * =========================
     */
    case "LOAD_TICKETS": {
      const newTickets = action.payload || [];

      if (newTickets.length === 0 && state.length > 0 && !action.isInitialLoad) {
        return state;
      }

      if (action.isInitialLoad) {
        return [...newTickets].sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
      }

      if (action.isLoadMore) {
        const ticketIds = new Set(state.map((t) => t.id));
        const uniqueNewTickets = newTickets.filter(
          (ticket) => !ticketIds.has(ticket.id)
        );
        return [...state, ...uniqueNewTickets];
      }

      const ticketIds = new Set(state.map((t) => t.id));
      const uniqueNewTickets = newTickets.filter(
        (ticket) => !ticketIds.has(ticket.id)
      );

      return [...state, ...uniqueNewTickets].sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    }

    case "APPEND_TICKETS": {
      const newTickets = action.payload || [];

      // Si no hay nuevos tickets, no hacer nada
      if (!newTickets.length) return state;

      const ticketIds = new Set(state.map(t => t.id));

      // Filtrar duplicados
      const uniqueNewTickets = newTickets.filter(
        ticket => !ticketIds.has(ticket.id)
      );

      // Si no hay nuevos únicos, regresar estado actual
      if (!uniqueNewTickets.length) return state;

      const merged = [...state, ...uniqueNewTickets];

      // Mantener orden por updatedAt (igual que en LOAD)
      return merged.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    }

    /**
     * =========================
     * UPDATE_TICKET
     * =========================
     */
    case "UPDATE_TICKET": {
      const deepMerge = (oldObj, newObj) => {
        const result = { ...oldObj };

        for (const key in newObj) {
          if (
            typeof newObj[key] === "object" &&
            newObj[key] !== null &&
            !Array.isArray(newObj[key])
          ) {
            result[key] = deepMerge(oldObj[key] || {}, newObj[key]);
          } else {
            result[key] = newObj[key];
          }
        }

        return result;
      };

      let ticket;
      let ticketId;

      // Soporta formato parcial
      if (action.payload.id && action.payload.changes) {
        ticketId = action.payload.id;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex === -1) return state;

        const existingTicket = state[ticketIndex];
        ticket = deepMerge(existingTicket, action.payload.changes);
      } else {
        ticket = action.payload;
        ticketId = ticket.id;
      }

      const ticketIndex = state.findIndex((t) => t.id === ticketId);

      if (ticketIndex !== -1) {
        const existingTicket = state[ticketIndex];
        const mergedTicket = action.payload.changes
          ? ticket
          : {
              ...existingTicket,
              ...ticket,
              whatsapp: ticket.whatsapp || existingTicket.whatsapp,
              contact: ticket.contact || existingTicket.contact,
              user: ticket.user !== undefined ? ticket.user : existingTicket.user,
              queue: ticket.queue || existingTicket.queue,
              tags: ticket.tags || existingTicket.tags,
              postponeUntil: Object.prototype.hasOwnProperty.call(ticket, "postponeUntil")
                ? ticket.postponeUntil
                : (existingTicket.postponeUntil ?? null),
            };

        const newState = [...state];
        newState[ticketIndex] = mergedTicket;

        const isReadOnlyUpdate =
          action.isReadOnlyUpdate ||
          (action.payload.changes &&
            Object.keys(action.payload.changes).every((key) =>
              ["unreadMessages", "lastMessage"].includes(key)
            ));

        const isUnreadOnlyUpdate =
          !action.payload.changes &&
          existingTicket.status === mergedTicket.status &&
          existingTicket.userId === mergedTicket.userId &&
          existingTicket.queueId === mergedTicket.queueId &&
          existingTicket.lastMessage === mergedTicket.lastMessage &&
          existingTicket.unreadMessages !== mergedTicket.unreadMessages;

        const isContactUpdate = action.isContactUpdate;

        if (isReadOnlyUpdate || isUnreadOnlyUpdate || isContactUpdate) {
          return newState;
        }

        return newState.sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
      }

      // Si no existe, agregarlo
      return [ticket, ...state].sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    }

    /**
     * =========================
     * DELETE_TICKET
     * =========================
     */
    case "DELETE_TICKET": {
      const ticketId =
        typeof action.payload === "object"
          ? action.payload.id
          : action.payload;

      return state.filter((ticket) => ticket.id !== ticketId);
    }

    /**
     * =========================
     * UPDATE_CONTACT_IN_TICKETS
     * =========================
     */
    case "UPDATE_CONTACT_IN_TICKETS": {
      const { contactId, updatedContact } = action.payload;

      return state.map((ticket) => {
        if (ticket.contact && ticket.contact.id === contactId) {
          return {
            ...ticket,
            contact: {
              ...ticket.contact,
              ...updatedContact,
            },
          };
        }
        return ticket;
      });
    }

    /**
     * =========================
     * OPTIMISTIC_ACCEPT_TICKET
     * =========================
     */
    case "OPTIMISTIC_ACCEPT_TICKET": {
      const { ticketId, userId, userData } = action.payload;

      return state.map((ticket) =>
        ticket.id === ticketId
          ? {
              ...ticket,
              status: "open",
              userId,
              user: userData,
              updatedAt: new Date().toISOString(),
              optimistic: true,
            }
          : ticket
      );
    }

    /**
     * =========================
     * OPTIMISTIC_RESOLVE_TICKET
     * =========================
     */
    case "OPTIMISTIC_RESOLVE_TICKET": {
      const { ticketId } = action.payload;

      return state.map((ticket) =>
        ticket.id === ticketId
          ? {
              ...ticket,
              status: "closed",
              updatedAt: new Date().toISOString(),
              optimistic: true,
            }
          : ticket
      );
    }

    /**
     * =========================
     * OPTIMISTIC_REMOVE_TICKET
     * =========================
     */
    case "OPTIMISTIC_REMOVE_TICKET": {
      const { ticketId } = action.payload;
      return state.filter((t) => t.id !== ticketId);
    }

    /**
     * =========================
     * OPTIMISTIC_ROLLBACK_TICKET
     * =========================
     */
    case "OPTIMISTIC_ROLLBACK_TICKET": {
      const { ticketId, originalTicket } = action.payload;

      const ticketIndex = state.findIndex((t) => t.id === ticketId);

      if (ticketIndex !== -1) {
        const newState = [...state];
        newState[ticketIndex] = {
          ...originalTicket,
          optimistic: false,
        };
        return newState;
      }

      if (originalTicket) {
        return [originalTicket, ...state].sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
      }

      return state;
    }

    /**
     * =========================
     * OPTIMISTIC_MARK_UNREAD
     * =========================
     */
    case "OPTIMISTIC_MARK_UNREAD": {
      const { ticketId } = action.payload;

      return state.map((ticket) =>
        ticket.id === ticketId
          ? {
              ...ticket,
              unreadMessages: 1,
              optimistic: true,
            }
          : ticket
      );
    }

    /**
     * =========================
     * RESET
     * =========================
     */
    case "RESET":
      return [];

    default:
      return state;
  }
}
